import Badge from "@/components/atoms/Badge";
import Container from "@/components/atoms/Container";
import AutoInvoiceForm, {
  AutoInvoiceProps,
  useAutoInvoiceForm,
} from "@/components/organisms/AutoInvoice/Form";
import CreditNoteForm, {
  CreditNoteFormProps,
  useCreditNoteForm,
} from "@/components/organisms/AutoInvoice/CreditNoteForm";
import ProformaForm, {
  ProformaProps,
  useProformaForm,
} from "@/components/organisms/AutoInvoice/ProformaForm";
import ManualInvoiceForm, {
  ManualInvoiceProps,
  useManualInvoiceForm,
} from "@/components/organisms/AutoInvoice/ManualInvoiceForm";
import Detail from "@/components/organisms/AutoInvoice/Detail";
import useAccess from "@/hooks/useAccess";
import AutoInvoice from "@/types/AutoInvoice";
import { Button, Text } from "@radix-ui/themes";
import { PenSquare, X, CheckCheck, Download, Activity, CaptionsOff, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import useMessageProps from "@/hooks/useMessageProps";
import { Tabs, Box } from "@radix-ui/themes";
import StandardBreadcrumb from "@/components/molecules/StandardBreadcrumb";
import { shortenText } from "@/lib/utils";
import ReasonModal from "@/components/organisms/AutoInvoice/ReasonModal";
import HistoryTab from "@/components/organisms/AutoInvoice/Tabs/HistoryTab";
import History from "@/types/History";
import SowsAmountsTab from "@/components/organisms/AutoInvoice/Tabs/SowsAmountsTab";

interface SowAmount {
  sow_id: string;
  period: string;
  amount: number;
}

export type AutoInvoicePageProps = (AutoInvoiceProps | CreditNoteFormProps | ProformaProps | ManualInvoiceProps) & {
  auto_invoice: AutoInvoice;
  redirect_props: any;
  histories: History[];
  sows_amounts: SowAmount[];
};

const ShowAutoInvoice = (
  {
    auto_invoice: autoInvoice,
    redirect_props,
    histories,
    sows_amounts,
    ...rest
  }: AutoInvoicePageProps) => {
    const form = autoInvoice.invoice_type === 'CREDIT NOTE' 
    ? useCreditNoteForm(autoInvoice) 
    : autoInvoice.invoice_type === 'PROFORMA INVOICE' 
    ? useProformaForm(autoInvoice)
    : autoInvoice.invoice_type === 'MANUAL INVOICE'
    ? useManualInvoiceForm(autoInvoice) 
    : useAutoInvoiceForm(autoInvoice);
  const [loading, setLoading] = useState(false);
  const [isInfoTab, setIsInfoTab] = useState(true);
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState(false);
  const tabRef = useRef(null);
  const [active, setActive] = useState("info");
  const [restPropsData, setRestPropsData] = useState(rest);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [sowAmounts, setSowAmounts] = useState(sows_amounts);
  const [isSendingApprove, setIsSendingApprove] = useState(false);

  const breadcrumbItems = [
    {
      label: "Auto Invoices",
      onClick: () => router.get("/auto_invoices"),
    },
    {
      label: autoInvoice.id.toString(),
    },
  ];
 
  useMessageProps({ redirect_props });

  const updateSowAmounts = (amounts: SowAmount[]) => {
    setSowAmounts(amounts);
  };

  const onSubmit = (data: any) => {
    router.put(
      `/auto_invoices/${autoInvoice.id}`,
      data,
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  useEffect(() => {
    if (!loading && redirect_props?.success) {
      setEdit(false);
    }
  }, [loading]);

  useEffect(() => {
    if (tabRef?.current)
      [...(tabRef?.current as any).children]
        .find((el: any) => el.dataset.state === "active")
        .scrollIntoView({
          inline: "center",
        });
  }, [active]);

  const handleTakeAction = () => {
    router.get(
      `/auto_invoices/${autoInvoice.id}/take_action`,
      {},
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  const handleApproveAutoInvoice = () => {
    router.get(
      `/auto_invoices/${autoInvoice.id}/approve`,
      {},
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  const handleDeclineAutoInvoice = (reason: string) => {
    router.post(
      `/auto_invoices/${autoInvoice.id}/decline`,
      {declined_reason: reason},
      {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      }
    );
  };

  const handleDownloadInvoice = () => {
    const url = `/auto_invoices/${autoInvoice.id}/download.pdf`;
    const newTab = window.open(url, "_blank");

    // Eğer yeni sekme açma engellenirse
    if (!newTab) {
      window.location.href = `/auto_invoices/${autoInvoice.id}/download.pdf`;
    }
  };

  const handleSendApprove = () => {
    if (isSendingApprove) return;

    setIsSendingApprove(true);

    router.get(
      `/auto_invoices/${autoInvoice.id}/send_approve`,
      {},
      {
        onStart: () => setLoading(true),
        onFinish: () => {
          setLoading(false);
          setIsSendingApprove(false);
        },
      }
    );
  };

  const handleReasonInput = (reason: string) => {
    setIsReasonModalOpen(false);
    handleDeclineAutoInvoice(reason);
  };

  const handleDeclineButtonClick = () => {
    setIsReasonModalOpen(true);
  };

  return (
    <>
      <Container>
        <ReasonModal
          isOpen={isReasonModalOpen}
          onOpenChange={setIsReasonModalOpen}
          reason_type="decline"
          onSubmitRequest={handleReasonInput}
        />
        <StandardBreadcrumb items={breadcrumbItems} />
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="flex items-center gap-2">
            {autoInvoice?.required_action ? (
              <div>
                <Badge
                  variant='destructive'
                >
                  {autoInvoice?.required_action + "Please take action!"}
                </Badge>
              </div>
            ) : (
              <Text size="6">{shortenText(autoInvoice.reference_no, 50)}  {autoInvoice.currency_type}</Text>
            )}
            
      
          </div>
          {isInfoTab && (
            <div className="flex">
              {autoInvoice?.required_action && (
                <div className="mr-2">
                  <Button
                    onClick={handleTakeAction}
                    disabled={!getAccess(["take_action_auto_invoice"])}
                  >
                    <>
                      <Activity size={16} />
                      <span>Take Required Action</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status == 'WAITING APPROVAL' && (
                <div className="mr-2">
                  <Button
                    onClick={handleApproveAutoInvoice}
                    disabled={!getAccess(["approve_auto_invoice"])}
                  >
                    <>
                      <CheckCheck size={16} />
                      <span>Approve</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status == 'WAITING APPROVAL' && (
                <div className="mr-2">
                  <Button
                    onClick={handleDeclineButtonClick}
                    disabled={!getAccess(["decline_auto_invoice"])}
                  >
                    <>
                      <CaptionsOff size={16} />
                      <span>Decline</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status != 'DECLINED' && (
                <div className="mr-2">
                  <Button
                    onClick={handleDownloadInvoice}
                    disabled={!getAccess(["download_auto_invoice"])}
                  >
                    <>
                      <Download size={16} />
                      <span>Download</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status == 'DRAFT' && (
                <div className="mr-2">
                  <Button
                    onClick={handleSendApprove}
                    disabled={!getAccess(["send_approve_auto_invoice"]) || isSendingApprove}
                  >
                    <>
                      <Send size={16} />
                      <span>Send Approve</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status == 'DRAFT' && (
                <div className="mr-2">
                  <Button
                    onClick={handleDeclineButtonClick}
                    disabled={!getAccess(["decline_auto_invoice"])}
                  >
                    <>
                      <CaptionsOff size={16} />
                      <span>Decline</span>
                    </>
                  </Button>
                </div>
              )}
              {autoInvoice.status == 'DRAFT' && (
                <div className="mr-2">
                  <Button
                    onClick={() => setEdit(!edit)}
                    disabled={!getAccess(["update_auto_invoice"])}
                  >
                    {edit ? (
                      <>
                        <X size={16} className="mr-2" /> <span>Cancel</span>
                      </>
                    ) : (
                      <>
                        <PenSquare size={16} />
                        <span>Edit</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
              
            </div>
            
          )}
        </div>
      </Container>
      <Tabs.Root
        value={active}
        onValueChange={(tab) => {
          const isInfoTab = tab === "info";
          setIsInfoTab(isInfoTab);
          if (edit && isInfoTab) setEdit(false);
          setActive(tab);
        }}
        className="account-tabs"
      >
        <Tabs.List className={"px-2"} ref={tabRef}>
          <Tabs.Trigger value="info">Information</Tabs.Trigger>
          <Tabs.Trigger value="updateHistroy">Update History</Tabs.Trigger>

          {autoInvoice.invoice_type === 'INVOICE' && autoInvoice.actual_total_amount != autoInvoice.total_amount && ["WAITING APPROVAL"].includes(autoInvoice.status) && (
            <Tabs.Trigger value="SowsAmountsTab">Sows Amounts</Tabs.Trigger>
          )}   
        </Tabs.List>

        <Box>
          <Tabs.Content value="info">
            <Container className="py-4">
            {edit ? (
              autoInvoice.invoice_type == 'INVOICE' ? (
                <AutoInvoiceForm
                  form={form}
                  edit={edit}
                  autoInvoice={autoInvoice}
                  onSubmit={onSubmit}
                  data={restPropsData}
                  loading={loading}
                  errors={redirect_props?.errors}
                />
              ) : (
                <CreditNoteForm
                  form={form}
                  edit={true}
                  autoInvoice={autoInvoice}
                  onSubmit={onSubmit}
                  data={rest}
                  loading={loading}
                  mode="create"
                  errors={redirect_props?.errors}
                />
              )
                
              ) : (
                <Detail autoInvoice={autoInvoice} />
              )}

            </Container>
          </Tabs.Content>

          <Tabs.Content value="updateHistroy">
            {getAccess(["listing_auto_invoice_histories"]) ? (
              <HistoryTab histories={histories} />
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>

          <Tabs.Content value="SowsAmountsTab">
            {getAccess(["set_invoice_sows_amounts"]) ? (
              <SowsAmountsTab  data={sowAmounts} invoiceId={autoInvoice.id} onUpdateSowAmounts={updateSowAmounts}/>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed text-center !block mt-32 text-lg">
                You are not authorized to view this content.
              </span>
            )}
          </Tabs.Content>
          
        </Box>
      </Tabs.Root>
    </>
  );
};

export default ShowAutoInvoice;